import { useState, useCallback, useRef } from "react";
import { Upload, X, File, Image as ImageIcon, FileText, AlertCircle, Eye, Zap } from "lucide-react";
import { IconButton } from "@/components/StandardButton";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getSessionId } from "@/utils/formDraftManager";
import { ImageLightbox } from "./ImageLightbox";
import { optimizeImage, validateImageFile as validateImage } from "@/utils/imageOptimization";
import { Progress } from "@/components/ui/progress";
import { logger } from "@/utils/logger";

export interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  url?: string;
  uploadedAt: Date;
}

interface FileUploadProps {
  onFilesChange: (files: UploadedFile[]) => void;
  maxFiles?: number;
  maxSizeBytes?: number;
  acceptedTypes?: string[];
  existingFiles?: UploadedFile[];
}

const ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const FileUpload = ({
  onFilesChange,
  maxFiles = 5,
  maxSizeBytes = MAX_FILE_SIZE,
  acceptedTypes = ACCEPTED_TYPES,
  existingFiles = [],
}: FileUploadProps) => {
  const [files, setFiles] = useState<UploadedFile[]>(existingFiles);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizationProgress, setOptimizationProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const updateFiles = useCallback((newFiles: UploadedFile[]) => {
    setFiles(newFiles);
    onFilesChange(newFiles);
  }, [onFilesChange]);

  const validateFile = (file: File): string | null => {
    if (file.size > maxSizeBytes) {
      return `File "${file.name}" is too large. Maximum size is ${Math.round(maxSizeBytes / 1024 / 1024)}MB`;
    }
    if (!acceptedTypes.includes(file.type)) {
      return `File type "${file.type}" is not supported`;
    }
    return null;
  };

  const uploadFile = async (file: File): Promise<UploadedFile | null> => {
    const sessionId = getSessionId();
    const fileId = `${Date.now()}_${Math.random().toString(36).substring(7)}`;
    
    let fileToUpload: File | Blob = file;
    let finalFileName = file.name;
    let finalFileSize = file.size;
    let finalMimeType = file.type;

    // Optimize images before upload
    if (file.type.startsWith('image/') && file.type !== 'image/svg+xml') {
      try {
        setOptimizing(true);
        setOptimizationProgress(30);
        
        const optimizationResult = await optimizeImage(file, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.85,
          format: 'webp',
        });
        
        setOptimizationProgress(70);
        
        fileToUpload = optimizationResult.blob;
        finalFileSize = optimizationResult.optimizedSize;
        finalMimeType = `image/${optimizationResult.format}`;
        
        // Update filename extension to match optimized format
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
        finalFileName = `${nameWithoutExt}.${optimizationResult.format}`;
        
        const savings = ((1 - optimizationResult.compressionRatio / 100) * 100).toFixed(0);
        
        setOptimizationProgress(100);
        setOptimizing(false);
        
        if (optimizationResult.compressionRatio > 10) {
          toast({
            title: "Image optimized",
            description: `Reduced size by ${savings}% for faster upload`,
          });
        }
      } catch (error) {
        logger.error('Optimization error', error);
        setOptimizing(false);
        // Continue with original file if optimization fails
      }
    }

    const filePath = `temp/${sessionId}/${fileId}_${finalFileName}`;

    try {
      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('feedback-attachments')
        .upload(filePath, fileToUpload, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Create attachment record
      const { data: attachmentData, error: dbError } = await supabase
        .from('feedback_attachments')
        .insert({
          session_id: sessionId,
          file_name: finalFileName,
          file_path: filePath,
          file_size: finalFileSize,
          mime_type: finalMimeType,
          is_temporary: true,
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // Get signed URL for preview
      const { data: urlData } = await supabase.storage
        .from('feedback-attachments')
        .createSignedUrl(filePath, 3600);

      return {
        id: attachmentData.id,
        name: finalFileName,
        size: finalFileSize,
        type: finalMimeType,
        url: urlData?.signedUrl,
        uploadedAt: new Date(),
      };
    } catch (error) {
      logger.error('Upload error', error);
      throw error;
    }
  };

  const handleFiles = async (fileList: FileList) => {
    if (files.length + fileList.length > maxFiles) {
      toast({
        title: "Too many files",
        description: `You can only upload up to ${maxFiles} files`,
        variant: "destructive",
      });
      return;
    }

    const filesToUpload = Array.from(fileList);
    
    // Validate all files first
    for (const file of filesToUpload) {
      const error = validateFile(file);
      if (error) {
        toast({
          title: "Invalid file",
          description: error,
          variant: "destructive",
        });
        return;
      }
    }

    setUploading(true);
    const uploadedFiles: UploadedFile[] = [];

    try {
      for (const file of filesToUpload) {
        const uploaded = await uploadFile(file);
        if (uploaded) {
          uploadedFiles.push(uploaded);
        }
      }

      const newFiles = [...files, ...uploadedFiles];
      updateFiles(newFiles);

      toast({
        title: "Files uploaded",
        description: `${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''} uploaded successfully`,
      });
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload files",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async (fileId: string) => {
    const fileToRemove = files.find(f => f.id === fileId);
    if (!fileToRemove) return;

    try {
      // Delete from database
      await supabase
        .from('feedback_attachments')
        .delete()
        .eq('id', fileId);

      // Note: Storage cleanup is handled by RLS policies and temp file cleanup function
      
      const newFiles = files.filter(f => f.id !== fileId);
      updateFiles(newFiles);

      toast({
        title: "File removed",
        description: `${fileToRemove.name} has been removed`,
      });
    } catch (error: any) {
      toast({
        title: "Failed to remove file",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [files]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <ImageIcon className="h-5 w-5" />;
    if (type === 'application/pdf') return <FileText className="h-5 w-5" />;
    return <File className="h-5 w-5" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImageFile = (type: string) => type.startsWith('image/');

  const openLightbox = (fileId: string) => {
    const imageFiles = files.filter(f => isImageFile(f.type) && f.url);
    const index = imageFiles.findIndex(f => f.id === fileId);
    if (index !== -1) {
      setLightboxIndex(index);
      setLightboxOpen(true);
    }
  };

  const imageFiles = files.filter(f => isImageFile(f.type) && f.url);

  return (
    <div className="space-y-3">
      {/* Image Lightbox */}
      <ImageLightbox
        images={imageFiles.map(f => ({ url: f.url!, name: f.name }))}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
      />
      {/* Upload Area */}
      <div
        className={cn(
          "border-2 border-dashed rounded-2xl p-6 text-center transition-colors cursor-pointer",
          dragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50",
          (uploading || optimizing) && "opacity-50 pointer-events-none"
        )}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleChange}
          className="hidden"
          disabled={uploading || optimizing || files.length >= maxFiles}
        />
        
        {optimizing ? (
          <>
            <Zap className="h-8 w-8 mx-auto mb-2 text-primary animate-pulse" />
            <p className="text-sm font-medium mb-2">Optimizing image...</p>
            <Progress value={optimizationProgress} className="w-full max-w-xs mx-auto" />
          </>
        ) : (
          <>
            <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm font-medium mb-1">
              {uploading ? "Uploading..." : "Drop files here or click to browse"}
            </p>
            <p className="text-xs text-muted-foreground">
              Images, PDFs, documents up to 10MB ({files.length}/{maxFiles} files)
            </p>
            <p className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1">
              <Zap className="h-3 w-3" />
              Images are automatically optimized for faster upload
            </p>
          </>
        )}
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file) => {
            const isImage = isImageFile(file.type);
            
            return (
              <div
                key={file.id}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-2xl border bg-muted/30 transition-colors",
                  isImage && file.url && "cursor-pointer hover:bg-muted/50"
                )}
                onClick={() => isImage && file.url && openLightbox(file.id)}
              >
                {/* File Icon or Image Thumbnail */}
                <div className="relative text-muted-foreground flex-shrink-0">
                  {isImage && file.url ? (
                    <div className="relative w-12 h-12 rounded overflow-hidden border">
                      <img 
                        src={file.url} 
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center">
                        <Eye className="h-5 w-5 text-white opacity-0 hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  ) : (
                    getFileIcon(file.type)
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(file.size)}
                    {isImage && " • Click to preview"}
                  </p>
                </div>
                
                <IconButton
                  type="button"
                  className="flex-shrink-0 h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(file.id);
                  }}
                >
                  <X className="h-4 w-4" />
                </IconButton>
              </div>
            );
          })}
        </div>
      )}

      {/* Help Text */}
      {files.length === 0 && (
        <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-2xl text-xs text-muted-foreground">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <p>
            Attachments are optional but can help provide context. Supported: Images (JPG, PNG, GIF, WebP), PDFs, Word/Excel documents, text files.
          </p>
        </div>
      )}
    </div>
  );
};
